/**
 * BizNearYou Verified Entity Model and Schema Generator
 *
 * Defines the single source of truth for the BizNearYou organization entity
 * and provides accurate, compliant JSON-LD structured data generation
 * without keyword stuffing or deceptive entity markup.
 */

export interface BizNearYouEntity {
  name: "BizNearYou";
  canonicalUrl: string;
  logoUrl: string;
  description: string;
  officialSocialProfiles: string[];
}

export const BIZNEARYOU_ENTITY: BizNearYouEntity = {
  name: "BizNearYou",
  canonicalUrl: "https://getbiznearyou.com",
  logoUrl: "https://getbiznearyou.com/icon.png",
  description: "BizNearYou is a local business discovery and outreach organization platform helping users search nearby businesses, review public signals, save records, and organize outreach.",
  officialSocialProfiles: [
    "https://x.com/biznearyou",
    "https://linkedin.com/company/biznearyou",
    "https://github.com/biznearyou"
  ]
};

/**
 * Generates an accurate Organization JSON-LD schema based on the entity definition.
 */
export function getOrganizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": BIZNEARYOU_ENTITY.name,
    "url": BIZNEARYOU_ENTITY.canonicalUrl,
    "logo": BIZNEARYOU_ENTITY.logoUrl,
    "description": BIZNEARYOU_ENTITY.description,
    "sameAs": BIZNEARYOU_ENTITY.officialSocialProfiles
  };
}

/**
 * Generates an accurate WebSite JSON-LD schema with optional Sitelinks Searchbox.
 */
export function getWebSiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": BIZNEARYOU_ENTITY.name,
    "url": `${BIZNEARYOU_ENTITY.canonicalUrl}/`,
    "description": BIZNEARYOU_ENTITY.description,
    "publisher": {
      "@type": "Organization",
      "name": BIZNEARYOU_ENTITY.name,
      "url": BIZNEARYOU_ENTITY.canonicalUrl,
      "logo": BIZNEARYOU_ENTITY.logoUrl
    },
    "potentialAction": {
      "@type": "SearchAction",
      "target": `${BIZNEARYOU_ENTITY.canonicalUrl}/find-jobs?q={search_term_string}`,
      "query-input": "required name=search_term_string"
    }
  };
}

/**
 * Generates SoftwareApplication JSON-LD schema for product capability routes.
 */
export function getSoftwareApplicationSchema(featureName?: string, featureDescription?: string) {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": featureName ? `${featureName} - ${BIZNEARYOU_ENTITY.name}` : BIZNEARYOU_ENTITY.name,
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "All Web Browsers",
    "url": BIZNEARYOU_ENTITY.canonicalUrl,
    "description": featureDescription || BIZNEARYOU_ENTITY.description,
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock"
    },
    "publisher": {
      "@type": "Organization",
      "name": BIZNEARYOU_ENTITY.name,
      "url": BIZNEARYOU_ENTITY.canonicalUrl
    }
  };
}
